import LayeredAutocompleteSelect from "../../components/LayeredAutocompleteSelect";
import React, { useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import { withEmphasis } from "../hitl/emphasis";
import {
  AnsweredPause,
  PAUSE_CANCEL_BUTTON,
  PAUSE_REPLY_BUTTON,
  PauseButton,
  PauseCardData,
  PauseField,
  actionPauseRequest,
  answeredFieldValue,
  answeredPause,
  isChatOnlyPause,
  pauseAnswer,
  pauseNote,
} from "../hitl/actionPause";

export type PauseCardAction = "CONTINUE" | "CANCEL";

export interface PauseCardProps {
  /** The human review whose information request carries the card (`form_type: "action_pause"`). */
  humanReview: any;
  /** `human_review_status` of the message: approved / canceled / scheduled mean answered. */
  status?: string;
  /** Local "already submitted" state from the bubble, for instant feedback before the server echoes. */
  reviewed?: boolean;
  /** Whether the controls are shown at all (e.g. hidden on an unanswered card deep in history). */
  showControls?: boolean;
  /** Called with the action and the information answers keyed by request id. */
  onAnswer: (action: PauseCardAction, answers: Record<string, string>) => void;
  /** `panel`: the app's chat card look. `inline`: the shared/extension bubble look. */
  appearance?: "panel" | "inline";
  headingIcon?: React.ReactNode;
  compact?: boolean;
  bodyFontSize?: number;
}

const ANSWERED_STATUSES = ["approved", "canceled", "scheduled"];

/** A choice with more options than this is a dropdown rather than a list of radio buttons. */
export const CHOICE_SELECT_THRESHOLD = 5;

/** The selection that stands for "Other" on a choice; it can never be one of the options sent. */
const OTHER_CHOICE = "\u0000other";

/** The hint on a card answered only from the chat. */
export const CHAT_ONLY_HINT = "Reply in the chat below";

/** What an answered card says when its answer was typed in the chat. */
export const ANSWERED_IN_CHAT_NOTE = "Answered in chat";

const disabledSx = {
  "&:disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
};

const buttonBaseSx = (compact: boolean) => ({
  minHeight: compact ? 32 : 41,
  fontSize: compact ? "13px" : "14px",
  p: compact ? "6px 12px !important" : "10px 16px !important",
  textTransform: "none",
  fontFamily: "var(--theo-font-sans)",
  fontWeight: 700,
  borderRadius: "6px",
});

const primarySx = (compact: boolean) => ({
  ...buttonBaseSx(compact),
  background: "#8062D9",
  boxShadow: "0 4px 12px rgba(128, 98, 217, 0.22)",
  color: "#FFFFFF !important",
  "&:hover": {
    background: "#6846BF",
    boxShadow: "0 6px 16px rgba(128, 98, 217, 0.28)",
  },
  ...disabledSx,
});

const secondarySx = (compact: boolean) => ({
  ...buttonBaseSx(compact),
  color: "#6846BF",
  borderColor: "#C9BCF0",
  "&:hover": { borderColor: "#8062D9", background: "#F5F2FE" },
});

const cancelSx = (compact: boolean, appearance: "panel" | "inline") =>
  appearance === "panel"
    ? {
        ...buttonBaseSx(compact),
        background: "linear-gradient(180deg, #B8B0A8 0%, #A89F97 100%)",
        boxShadow: "0px 4px 16px 0px #B8B0A840",
        color: "#FFFFFF !important",
        "&:hover": {
          background: "linear-gradient(180deg, #A89F97 0%, #B8B0A8 100%)",
          boxShadow: "0px 6px 20px 0px #B8B0A860",
        },
        ...disabledSx,
      }
    : {
        ...buttonBaseSx(compact),
        background: "#64748B",
        boxShadow: "0 4px 12px rgba(71, 85, 105, 0.18)",
        color: "#FFFFFF",
        "&:hover": {
          background: "#475569",
          boxShadow: "0 6px 16px rgba(71, 85, 105, 0.24)",
        },
        ...disabledSx,
      };

interface PauseFieldInputProps {
  field: PauseField;
  /** The accessible name: the field's label, else the card's title. */
  name: string;
  /** For a text field, its text. For a choice, the option picked, or `OTHER_CHOICE`. */
  value: string;
  /** The text typed for "Other"; only read while "Other" is picked. */
  otherText: string;
  /** Whether a choice offers "Other" (the card's `other` flag). */
  allowOther: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  onOtherTextChange: (text: string) => void;
}

/**
 * One field: a text box for text and email, radio buttons (or a dropdown) for a choice. A choice
 * on a card with `other` gets one more entry, "Other", which reveals a text box for the answer.
 */
const PauseFieldInput: React.FC<PauseFieldInputProps> = ({
  field,
  name,
  value,
  otherText,
  allowOther,
  disabled,
  onChange,
  onOtherTextChange,
}) => {
  if (field.type !== "choice") {
    return (
      <TextField
        fullWidth
        size="small"
        type={field.type === "email" ? "email" : "text"}
        placeholder={field.placeholder || ""}
        inputProps={{ "aria-label": name }}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
    );
  }
  const otherBox =
    allowOther && value === OTHER_CHOICE ? (
      <TextField
        fullWidth
        size="small"
        autoFocus={!disabled}
        placeholder="Type your answer"
        inputProps={{ "aria-label": `${name}: other` }}
        value={otherText}
        onChange={(event) => onOtherTextChange(event.target.value)}
        disabled={disabled}
        sx={{ mt: 1 }}
      />
    ) : null;
  if (field.options.length > CHOICE_SELECT_THRESHOLD) {
    return (
      <>
        <LayeredAutocompleteSelect
          label=""
          fullWidth
          size="small"
          value={value}
          onChange={value => onChange(value)}
          disabled={disabled}
          inputAriaLabel={name}
          placeholder={field.placeholder || "Choose one"}
          disableClearable
          options={[...field.options.map(option => ({ value: option, label: option })), ...(allowOther ? [{ value: OTHER_CHOICE, label: "Other" }] : [])]}
        />
        {otherBox}
      </>
    );
  }
  const radio = (option: string, label: string) => (
    <FormControlLabel
      key={option}
      value={option}
      disabled={disabled}
      control={<Radio size="small" />}
      label={
        <Typography fontSize={14} fontFamily="var(--theo-font-sans)" color="#374151">
          {label}
        </Typography>
      }
    />
  );
  return (
    <>
      <RadioGroup aria-label={name} value={value} onChange={(event) => onChange(event.target.value)}>
        {field.options.map((option) => radio(option, option))}
        {allowOther ? radio(OTHER_CHOICE, "Other") : null}
      </RadioGroup>
      {otherBox}
    </>
  );
};

/**
 * A stored answer, read back into what the inputs show: a choice answered off its options was an
 * "Other", so it shows as "Other" with its text.
 */
const storedSelection = (field: PauseField, answer: string, allowOther: boolean): { value: string; otherText: string } =>
  field.type === "choice" && allowOther && answer !== "" && !field.options.includes(answer)
    ? { value: OTHER_CHOICE, otherText: answer }
    : { value: answer, otherText: "" };

/** Each field's answer, trimmed, by field id: for "Other", the text typed for it. */
const fieldValues = (
  card: PauseCardData,
  typed: Record<string, string>,
  otherTyped: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(
    card.fields.map((field) => {
      const picked = typed[field.id] ?? "";
      return [field.id, (picked === OTHER_CHOICE ? otherTyped[field.id] ?? "" : picked).trim()];
    })
  );

/**
 * One renderer for every action pause. The card (title, body, buttons, fields, notes) comes
 * entirely from `card_json`; nothing is read from the request id.
 *
 * Fields make it a form: every one is required, so the primary button waits until each has an
 * answer ("Other" counts once its text is typed). An answered card shows the answers read-only,
 * from the stored answer.
 *
 * A chat-only card (`chat_reply` with no fields) has no inputs: the chat box answers it, and the
 * reply is the user message under it, so the card says where to answer and offers only its Cancel.
 */
const PauseCard: React.FC<PauseCardProps> = ({
  humanReview,
  status,
  reviewed = false,
  showControls = true,
  onAnswer,
  appearance = "panel",
  headingIcon,
  compact = false,
  bodyFontSize = 14,
}) => {
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [otherTyped, setOtherTyped] = useState<Record<string, string>>({});
  const [pressed, setPressed] = useState<AnsweredPause | null>(null);

  const pause = actionPauseRequest(humanReview);
  if (!pause) return null;
  const { request, card } = pause;

  const device = card.device_status;
  const offline = card.stop_turn && device?.online === false;
  const isAnswered = reviewed || ANSWERED_STATUSES.includes(String(status || ""));
  const stored = answeredPause(humanReview);
  // The stored answer wins (it survives a reload); the local press covers the moment before the
  // server echoes it back.
  const answer: AnsweredPause | null = stored ?? (isAnswered ? pressed : null);
  const showOffline = offline && !isAnswered && !answer;
  const isWait = card.template === "wait";
  const pendingStatus = showOffline ? "Waiting for device to come online." : card.pending_status;
  const chosenButton = answer?.button ?? (status === "canceled" ? PAUSE_CANCEL_BUTTON : null);
  const answeredInChat = answer?.button === PAUSE_REPLY_BUTTON;
  const note =
    pauseNote(card, answer) ?? (answeredInChat ? { text: ANSWERED_IN_CHAT_NOTE, declined: false } : null);
  const chatOnly = isChatOnlyPause(card);

  const hasFields = card.fields.length > 0;
  const currentValues = fieldValues(card, typed, otherTyped);
  const allFilled = card.fields.every((field) => currentValues[field.id].length > 0);
  // A lone field keeps the plain look it has always had; a form (or a question Theo asks, which
  // can be answered from the chat) labels each -- unless the label only repeats the body above it.
  const showFieldLabels = card.fields.length > 1 || (card.chat_reply && card.fields[0]?.label !== card.body);

  const press = (button: PauseButton) => {
    if (isAnswered) return;
    const isCancel = button.id === PAUSE_CANCEL_BUTTON;
    const values = isCancel ? {} : currentValues;
    const value = hasFields && !isCancel ? values[card.fields[0].id] ?? "" : "";
    setPressed({ button: button.id, value, values });
    onAnswer(isCancel ? "CANCEL" : "CONTINUE", {
      [String(request.id ?? "")]: pauseAnswer(card, button.id, value, values),
    });
  };

  const visibleButtons = !showControls
    ? []
    : isAnswered
      ? card.buttons.filter((button) => button.id === chosenButton)
      : card.buttons;

  const headingSx =
    appearance === "panel"
      ? { fontSize: 16, fontWeight: 600, color: "#172033" }
      : { fontSize: 14, fontWeight: 500, color: "#F0A55E" };

  return (
    <Box data-testid="pause-card">
      <Box display="flex" alignItems="center" mb={appearance === "panel" ? 2 : 1}>
        {headingIcon}
        <Typography fontFamily="var(--theo-font-sans)" sx={headingSx}>
          {card.title}
        </Typography>
      </Box>
      {card.body ? <Typography mb={isWait ? 1 : 2} fontSize={bodyFontSize} fontWeight={500} color="#666666" fontFamily="var(--theo-font-sans)">
        {withEmphasis(card.body)}
      </Typography> : null}
      {isWait && !isAnswered && !answer && pendingStatus ? (
        <Typography role="status" mb={1} fontSize={bodyFontSize} color="#666666" fontFamily="var(--theo-font-sans)">
          {pendingStatus}
        </Typography>
      ) : null}
      {showOffline ? (
        <Box sx={{ mb: 1 }}>
          {device.last_seen_at ? <Typography fontSize={13} color="#666666" sx={{ mt: 1 }}>
            Last seen {new Date(device.last_seen_at).toLocaleString()}
          </Typography> : null}
          <Typography fontSize={13} color="#666666" sx={{ mt: 1 }}>
            Submitted Datto jobs may still run after Stop.
          </Typography>
        </Box>
      ) : null}
      {chatOnly && !isAnswered && showControls ? (
        <Typography mb={visibleButtons.length > 0 ? 2 : 0} fontSize={13} fontWeight={600} color="#6846BF" fontFamily="var(--theo-font-sans)">
          {CHAT_ONLY_HINT}
        </Typography>
      ) : null}
      {card.fields.map((field, index) => {
        const shown =
          isAnswered && answer
            ? storedSelection(field, answeredFieldValue(answer, field, index), card.other)
            : { value: typed[field.id] ?? "", otherText: otherTyped[field.id] ?? "" };
        return (
          <Box key={field.id} mb={2}>
            {showFieldLabels && field.label ? (
              <Typography mb={0.75} fontSize={14} fontWeight={600} color="#374151" fontFamily="var(--theo-font-sans)">
                {card.fields.length > 1 ? `${index + 1}. ${field.label}` : field.label}
              </Typography>
            ) : null}
            <PauseFieldInput
              field={field}
              name={field.label || card.title}
              value={shown.value}
              otherText={shown.otherText}
              allowOther={card.other}
              onChange={(value) => setTyped((previous) => ({ ...previous, [field.id]: value }))}
              onOtherTextChange={(text) => setOtherTyped((previous) => ({ ...previous, [field.id]: text }))}
              disabled={isAnswered || !showControls}
            />
          </Box>
        );
      })}
      {visibleButtons.length > 0 ? (
        <Stack direction="row" spacing={compact ? 1.5 : 2} flexWrap="wrap" useFlexGap>
          {visibleButtons.map((button) => {
            const isCancel = button.id === PAUSE_CANCEL_BUTTON;
            const needsAnswers = hasFields && !isCancel && button.primary;
            const disabled = isAnswered || (needsAnswers && !allFilled);
            if (isCancel) {
              return (
                <Button
                  key={button.id}
                  variant="contained"
                  startIcon={<CloseIcon sx={{ fontSize: compact ? 16 : 20 }} />}
                  disabled={disabled}
                  onClick={() => press(button)}
                  sx={cancelSx(compact, appearance)}
                >
                  {button.label}
                </Button>
              );
            }
            if (button.primary) {
              return (
                <Button
                  key={button.id}
                  variant="contained"
                  startIcon={<CheckIcon sx={{ fontSize: compact ? 16 : 20 }} />}
                  disabled={disabled}
                  onClick={() => press(button)}
                  sx={primarySx(compact)}
                >
                  {button.label}
                </Button>
              );
            }
            return (
              <Button
                key={button.id}
                variant="outlined"
                disabled={disabled}
                onClick={() => press(button)}
                sx={secondarySx(compact)}
              >
                {button.label}
              </Button>
            );
          })}
        </Stack>
      ) : null}
      {note ? (
        <Typography
          mt={1}
          fontSize={13}
          fontWeight={600}
          color={note.declined ? "#B91C1C" : "#15803D"}
          fontFamily="var(--theo-font-sans)"
        >
          {note.text}
        </Typography>
      ) : null}
    </Box>
  );
};

export default PauseCard;
